import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { HERO_IMG } from "../lib/categories";

export default function Auth() {
  const { user, login, register, loginGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(location.pathname === "/register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setMode(location.pathname === "/register" ? "register" : "login"); }, [location.pathname]);
  useEffect(() => { if (user && user.role) navigate(user.role === "admin" ? "/admin" : "/account"); }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (mode === "register") await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-[#FBF5DD] border border-[#0D530E]/15 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#306D29] focus:outline-none";

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <div className="grid grid-cols-1 md:grid-cols-2 rounded-[32px] overflow-hidden border border-[#0D530E]/10">
        <div className="relative hidden md:block">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D530E]/60" />
          <div className="relative z-10 p-12 h-full flex flex-col justify-end">
            <h2 className="font-display text-4xl font-light text-[#FBF5DD] leading-tight">Your world, delivered home</h2>
            <p className="text-[#FBF5DD]/80 mt-3 font-light">Track orders, manage subscriptions and reorder favourites in one place.</p>
          </div>
        </div>
        <div className="bg-[#FBF5DD] p-8 sm:p-12">
          <h1 className="font-display text-4xl font-light text-[#0D530E] mb-2">{mode === "register" ? "Create your account" : "Welcome back"}</h1>
          <p className="text-[#306D29]/80 font-light mb-8">{mode === "register" ? "Join AfriGate to shop and track your orders." : "Sign in to view your orders and subscriptions."}</p>
          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <input required className={input} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="auth-name" />
            )}
            <input required type="email" className={input} placeholder="Email" value={form.email} onChange={set("email")} data-testid="auth-email" />
            <input required type="password" minLength={6} className={input} placeholder="Password" value={form.password} onChange={set("password")} data-testid="auth-password" />
            {error && <p className="text-[#B0341F] text-sm" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full bg-[#306D29] hover:bg-[#0D530E] text-[#FBF5DD] font-medium py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}>
              {loading && <Loader2 size={18} className="animate-spin" />}
              {mode === "register" ? "Create account" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-[#0D530E]/10 flex-1" />
            <span className="text-xs text-[#306D29]/60 uppercase tracking-wider">or</span>
            <div className="h-px bg-[#0D530E]/10 flex-1" />
          </div>

          <button type="button" onClick={loginGoogle} data-testid="google-signin"
            className="w-full bg-white border border-[#0D530E]/15 text-[#0D530E] font-medium py-3.5 rounded-full flex items-center justify-center gap-3 hover:border-[#306D29]"
            style={{ transition: "border-color 0.2s ease" }}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>
          <p className="text-sm text-[#306D29]/80 mt-6">
            {mode === "register" ? "Already have an account? " : "New to AfriGate? "}
            <Link to={mode === "register" ? "/login" : "/register"} data-testid="auth-switch" className="font-semibold text-[#306D29] underline">
              {mode === "register" ? "Sign in" : "Create one"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
